/**
 * PersonalBuildGizmoModule — Build Mode v2: professional in-world editing for
 * any world the player can BUILD in (personal worlds, owned/granted worlds,
 * and — for ADMINS ONLY — the Commons; `canBuildIn` resolves build|admin caps
 * from the replicated Permission Matrix and the server re-gates every edit).
 *
 * Design follows the level-editor invariants (command-stack research, 2026):
 *   • ONE mutation path — every place/move/delete/duplicate flows through
 *     `BuildCommandStack` (lib/build_commands), so Ctrl+Z/Ctrl+Y are total.
 *   • A gizmo drag is ONE transaction: before-state captured at drag start,
 *     one EditPartCmd pushed at drag end. Inspector nudges coalesce.
 *   • The Build Editor window is a PROJECTION of replicated rows (outliner,
 *     inspector, budget meter, validation) — it holds no document state.
 *   • Budgets are ALWAYS visible while editing (LI used/max meter).
 *   • Validation runs at edit time (floating/buried/out-of-bounds/over-budget)
 *     with click-to-select — not at publish time.
 *
 * Keys (industry conventions + legacy aliases):
 *   B                enter/exit build edit  ·  Esc  deselect, then exit
 *   click            select part  ·  W/E/R (or 1/2/3) translate/rotate/scale
 *   Q                deselect (select-tool)  ·  F    face the selected part
 *   X                toggle snapping (1 m / 15° / 0.25)
 *   Ctrl+Z / Ctrl+Y  undo / redo (Ctrl+Shift+Z also redoes)
 *   Ctrl+D           duplicate selection (+0.5 m X)
 *   End              drop selection to the ground under it
 *   Delete           delete selection (undoable — catalog/prim parts re-rez)
 */

import * as THREE from 'three/webgpu';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { FreeformBuildModule } from './FreeformBuildModule.js';
import { canBuildIn } from '../lib/world_caps.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import {
  BuildCommandStack, EditPartCmd, DeletePartCmd, RezCmd, GroupCmd,
  snapshotPart, type PartRowSnapshot,
} from '../lib/build_commands.js';

interface PartRowLike {
  partId: bigint; worldId: bigint; primType: string; meshRef: string;
  posX: number; posY: number; posZ: number;
  rotX: number; rotY: number; rotZ: number; rotW: number;
  scaleX: number; scaleY: number; scaleZ: number;
  shapeJson: string; materialJson: string; physics: boolean;
  landImpact: number;
}

interface Issue { severity: 'warn' | 'error'; text: string; partKey: string | null }

export class PersonalBuildGizmoModule implements IModule {
  readonly name = 'PersonalBuildGizmo';

  private scene!: THREE.Scene;
  private canvas!: HTMLCanvasElement;
  private editing = false;
  private selectedKey: string | null = null; // primary selection
  private readonly selectedKeys = new Set<string>();
  private pivot: THREE.Object3D | null = null;
  private multiBefore: Map<string, { snap: PartRowSnapshot; pos: THREE.Vector3; quat: THREE.Quaternion; scale: THREE.Vector3 }> | null = null;
  private pivotStart: { pos: THREE.Vector3; quat: THREE.Quaternion; scale: THREE.Vector3 } | null = null;
  private marquee: { sx: number; sy: number; el: HTMLDivElement } | null = null;
  private gizmo: TransformControls | null = null;
  private gizmoHelper: THREE.Object3D | null = null;
  private snapEnabled = true;
  private dragBefore: PartRowSnapshot | null = null;
  private readonly stack = new BuildCommandStack();
  private win: UiWindow | null = null;
  private els: Record<string, HTMLElement> = {};
  private outlinerFilter = '';
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();

  constructor(private readonly freeform: FreeformBuildModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    this.canvas = app.renderer.native.domElement as HTMLCanvasElement;

    window.addEventListener('keydown', (e) => this.onKey(e));
    this.canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    bus.on('atlas:world-cleared', () => this.setEditing(false));
    bus.on('atlas:freeform-updated', () => { if (this.editing) this.renderPanel(); });
    bus.on('atlas:build-history-changed', () => { if (this.editing) this.renderHistory(); });
  }

  /** Buildable = build|admin cap in this world. In the COMMONS only admins pass
   *  (`canBuildIn` reads the Permission Matrix; ordinary Commons visitors hold
   *  no build role there). The server re-gates every reducer regardless. */
  private canEditHere(): boolean {
    const wid = atlas.currentWorldId;
    if (wid === null || !atlas.conn) return false;
    return canBuildIn(wid);
  }

  // ─── Mode ─────────────────────────────────────────────────────────────────────

  private setEditing(on: boolean): void {
    if (this.editing === on) return;
    if (on && !this.canEditHere()) {
      bus.emit('atlas:status', { text: 'Build edit (B) needs build rights here (your worlds, a build grant, or Commons admin).' });
      return;
    }
    this.editing = on;
    if (on) {
      atlas.editorCursorMode = true;
      if (document.pointerLockElement) document.exitPointerLock();
      this.openPanel();
      bus.emit('atlas:status', { text: 'Build edit ON — click a part · W/E/R modes · Ctrl+Z undo · B exits.' });
    } else {
      this.deselect();
      this.stack.clear();
      this.win?.setOpen(false);
      atlas.editorCursorMode = false;
      bus.emit('atlas:status', { text: 'Build edit off.' });
    }
  }

  // ─── Selection ────────────────────────────────────────────────────────────────

  private onPointerDown(e: PointerEvent): void {
    if (!this.editing || e.button !== 0) return;
    if (this.gizmo?.dragging) return;
    const cam = atlas.localMesh?.children[0] as THREE.PerspectiveCamera | undefined;
    if (!cam) return;
    const r = this.canvas.getBoundingClientRect();
    this.pointer.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, cam);
    const hits = this.raycaster.intersectObjects(this.freeform.root.children, true);
    for (const h of hits) {
      let o: THREE.Object3D | null = h.object;
      while (o && o.userData.ffPartId === undefined) o = o.parent;
      if (o?.userData.ffPartId) {
        const key = o.userData.ffPartId as string;
        if (e.shiftKey) {
          // Shift-click: toggle membership in the multi-selection.
          const keys = new Set(this.selectedKeys);
          if (keys.has(key)) keys.delete(key); else keys.add(key);
          this.setSelection([...keys], key);
        } else {
          this.setSelection([key], key);
        }
        return;
      }
    }
    // Empty space → rubber-band marquee (screen-space; selects on release).
    this.startMarquee(e);
  }

  // ─── Marquee (rubber-band) multi-select ───────────────────────────────────────

  private startMarquee(e: PointerEvent): void {
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;border:1px dashed var(--ax-accent);background:color-mix(in srgb, var(--ax-accent) 13.3%, transparent);z-index:9999;pointer-events:none';
    document.body.appendChild(el);
    this.marquee = { sx: e.clientX, sy: e.clientY, el };
    const move = (ev: PointerEvent): void => {
      const m = this.marquee;
      if (!m) return;
      const x = Math.min(m.sx, ev.clientX), y = Math.min(m.sy, ev.clientY);
      const w = Math.abs(ev.clientX - m.sx), h = Math.abs(ev.clientY - m.sy);
      m.el.style.left = `${x}px`; m.el.style.top = `${y}px`;
      m.el.style.width = `${w}px`; m.el.style.height = `${h}px`;
    };
    const up = (ev: PointerEvent): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const m = this.marquee;
      this.marquee = null;
      if (!m) return;
      m.el.remove();
      const x0 = Math.min(m.sx, ev.clientX), x1 = Math.max(m.sx, ev.clientX);
      const y0 = Math.min(m.sy, ev.clientY), y1 = Math.max(m.sy, ev.clientY);
      if (x1 - x0 < 6 && y1 - y0 < 6) { this.deselect(); return; } // a click, not a drag
      this.marqueeSelect(x0, y0, x1, y1, ev.shiftKey);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  /** Select every part whose projected center falls inside the screen rect. */
  private marqueeSelect(x0: number, y0: number, x1: number, y1: number, additive: boolean): void {
    const cam = atlas.localMesh?.children[0] as THREE.PerspectiveCamera | undefined;
    if (!cam) return;
    const r = this.canvas.getBoundingClientRect();
    const v = new THREE.Vector3();
    const keys = additive ? new Set(this.selectedKeys) : new Set<string>();
    for (const p of this.worldParts()) {
      const key = p.partId.toString();
      const mesh = this.meshForKey(key);
      if (!mesh) continue;
      mesh.getWorldPosition(v).project(cam);
      if (v.z > 1) continue; // behind the camera
      const sx = r.left + ((v.x + 1) / 2) * r.width;
      const sy = r.top + ((1 - v.y) / 2) * r.height;
      if (sx >= x0 && sx <= x1 && sy >= y0 && sy <= y1) keys.add(key);
    }
    if (keys.size) this.setSelection([...keys], [...keys][keys.size - 1]);
    else if (!additive) this.deselect();
  }

  private meshForKey(key: string): THREE.Object3D | null {
    let found: THREE.Object3D | null = null;
    this.freeform.root.traverse((o) => {
      if (!found && o.userData.ffPartId === key) found = o;
    });
    return found;
  }

  private rowForKey(key: string): PartRowLike | null {
    const conn = atlas.conn;
    if (!conn) return null;
    return (conn.db.part.partId.find(BigInt(key)) as unknown as PartRowLike) ?? null;
  }

  private select(key: string, mesh?: THREE.Object3D | null): void {
    void mesh;
    this.setSelection([key], key);
  }

  /** The one selection entry point: 1 key → gizmo on the mesh; N keys → gizmo
   *  on a pivot at the selection centroid (grouped transforms). */
  private setSelection(keys: string[], primary: string): void {
    const live = keys.filter((k) => this.meshForKey(k));
    if (!live.length) { this.deselect(); return; }
    this.ensureGizmo();
    if (!this.gizmo) return;
    this.selectedKeys.clear();
    for (const k of live) this.selectedKeys.add(k);
    this.selectedKey = live.includes(primary) ? primary : live[live.length - 1];
    atlas.gizmoHeldPartId = this.selectedKey;
    if (live.length === 1) {
      if (this.pivot) { this.scene.remove(this.pivot); this.pivot = null; }
      this.gizmo.attach(this.meshForKey(live[0])!);
    } else {
      const centroid = new THREE.Vector3();
      const v = new THREE.Vector3();
      for (const k of live) { this.meshForKey(k)!.getWorldPosition(v); centroid.add(v); }
      centroid.divideScalar(live.length);
      if (!this.pivot) { this.pivot = new THREE.Object3D(); this.scene.add(this.pivot); }
      this.pivot.position.copy(centroid);
      this.pivot.quaternion.identity();
      this.pivot.scale.set(1, 1, 1);
      this.gizmo.attach(this.pivot);
      bus.emit('atlas:status', { text: `${live.length} parts selected — grouped transform (one undo step).` });
    }
    this.applySnap();
    this.renderPanel();
  }

  private deselect(): void {
    this.gizmo?.detach();
    if (this.pivot) { this.scene.remove(this.pivot); this.pivot = null; }
    this.selectedKey = null;
    this.selectedKeys.clear();
    atlas.gizmoHeldPartId = null;
    this.dragBefore = null;
    this.multiBefore = null;
    this.pivotStart = null;
    bus.emit('atlas:freeform-updated', {});
  }

  // ─── Gizmo (drag = one undo transaction) ──────────────────────────────────────

  private ensureGizmo(): void {
    if (this.gizmo) return;
    const body = atlas.localMesh;
    if (!body) return;
    const cam = body.children[0] as unknown as import('three').Camera;
    const gizmo = new TransformControls(cam, this.canvas);
    gizmo.setMode('translate');
    gizmo.addEventListener('dragging-changed', (e) => {
      if (e.value) {
        if (this.selectedKeys.size > 1 && this.pivot) {
          // Grouped transaction: capture each member + the pivot start.
          this.multiBefore = new Map();
          for (const k of this.selectedKeys) {
            const row = this.rowForKey(k);
            const mesh = this.meshForKey(k);
            if (!row || !mesh) continue;
            this.multiBefore.set(k, {
              snap: snapshotPart(row),
              pos: mesh.position.clone(),
              quat: mesh.quaternion.clone(),
              scale: mesh.scale.clone(),
            });
          }
          this.pivotStart = {
            pos: this.pivot.position.clone(),
            quat: this.pivot.quaternion.clone(),
            scale: this.pivot.scale.clone(),
          };
        } else {
          const row = this.selectedKey ? this.rowForKey(this.selectedKey) : null;
          this.dragBefore = row ? snapshotPart(row) : null;
        }
      } else {
        this.commitDrag();
      }
    });
    gizmo.addEventListener('objectChange', () => {
      if (this.selectedKeys.size > 1) this.applyPivotDelta();
      this.renderInspectorNumbers();
    });
    const helper = gizmo.getHelper();
    this.scene.add(helper);
    this.gizmo = gizmo;
    this.gizmoHelper = helper;
  }

  private applySnap(): void {
    if (!this.gizmo) return;
    this.gizmo.setTranslationSnap(this.snapEnabled ? 1 : null);
    this.gizmo.setRotationSnap(this.snapEnabled ? THREE.MathUtils.degToRad(15) : null);
    this.gizmo.setScaleSnap(this.snapEnabled ? 0.25 : null);
    this.renderPanel();
  }

  /** Live during a multi-drag: apply the pivot's delta to every member. */
  private applyPivotDelta(): void {
    const pv = this.pivot;
    const start = this.pivotStart;
    const before = this.multiBefore;
    if (!pv || !start || !before) return;
    const dq = pv.quaternion.clone().multiply(start.quat.clone().invert());
    const ds = pv.scale.x / Math.max(1e-6, start.scale.x); // uniform group scale
    const off = new THREE.Vector3();
    for (const [k, b] of before) {
      const mesh = this.meshForKey(k);
      if (!mesh) continue;
      off.copy(b.pos).sub(start.pos).multiplyScalar(ds).applyQuaternion(dq);
      mesh.position.copy(pv.position).add(off);
      mesh.quaternion.copy(dq).multiply(b.quat);
      mesh.scale.copy(b.scale).multiplyScalar(ds);
    }
  }

  /** Multi-drag end → N edits as ONE GroupCmd undo step. */
  private commitMultiDrag(): void {
    const before = this.multiBefore;
    this.multiBefore = null;
    this.pivotStart = null;
    if (!before) return;
    const cmds: EditPartCmd[] = [];
    for (const [k, b] of before) {
      const mesh = this.meshForKey(k);
      if (!mesh) continue;
      const after: PartRowSnapshot = {
        ...b.snap,
        posX: mesh.position.x, posY: mesh.position.y, posZ: mesh.position.z,
        rotX: mesh.quaternion.x, rotY: mesh.quaternion.y, rotZ: mesh.quaternion.z, rotW: mesh.quaternion.w,
        scaleX: mesh.scale.x, scaleY: mesh.scale.y, scaleZ: mesh.scale.z,
      };
      cmds.push(new EditPartCmd(`Move #${b.snap.partId}`, b.snap, after));
    }
    if (!cmds.length) return;
    const group = new GroupCmd(`Move ${cmds.length} parts`, cmds);
    group.execute()
      .then(() => this.stack.push(group))
      .catch((err: Error) => bus.emit('atlas:status', { text: `Group edit rejected: ${err.message.slice(0, 100)}` }));
  }

  /** Drag end → ONE command already-applied on the server via editPart. */
  private commitDrag(): void {
    if (this.selectedKeys.size > 1) { this.commitMultiDrag(); return; }
    const key = this.selectedKey;
    const before = this.dragBefore;
    this.dragBefore = null;
    if (!key || !before) return;
    const mesh = this.gizmo?.object;
    if (!mesh) return;
    const after: PartRowSnapshot = {
      ...before,
      posX: mesh.position.x, posY: mesh.position.y, posZ: mesh.position.z,
      rotX: mesh.quaternion.x, rotY: mesh.quaternion.y, rotZ: mesh.quaternion.z, rotW: mesh.quaternion.w,
      scaleX: mesh.scale.x, scaleY: mesh.scale.y, scaleZ: mesh.scale.z,
    };
    const cmd = new EditPartCmd(`Move ${before.primType} #${before.partId}`, before, after);
    cmd.execute()
      .then(() => this.stack.push(cmd))
      .catch((err: Error) => {
        bus.emit('atlas:status', { text: `Edit rejected: ${err.message.slice(0, 100)}` });
      });
  }

  /** Apply an inspector/numeric edit through the stack (coalesces). */
  private applyEdit(mutate: (after: PartRowSnapshot) => void, label: string): void {
    const key = this.selectedKey;
    if (!key) return;
    const row = this.rowForKey(key);
    if (!row) return;
    const before = snapshotPart(row);
    const after = { ...before };
    mutate(after);
    void this.stack.run(new EditPartCmd(label, before, after)).catch((err: Error) => {
      bus.emit('atlas:status', { text: `Edit rejected: ${err.message.slice(0, 100)}` });
    });
  }

  // ─── Actions ──────────────────────────────────────────────────────────────────

  private deleteSelected(): void {
    const keys = [...this.selectedKeys];
    if (!keys.length) return;
    const cmds: DeletePartCmd[] = [];
    for (const k of keys) {
      const row = this.rowForKey(k);
      if (row) cmds.push(new DeletePartCmd(snapshotPart(row)));
    }
    if (!cmds.length) return;
    this.deselect();
    const cmd = cmds.length === 1 ? cmds[0] : new GroupCmd(`Delete ${cmds.length} parts`, cmds);
    void this.stack.run(cmd).catch((err: Error) => {
      bus.emit('atlas:status', { text: `Delete rejected: ${err.message.slice(0, 100)}` });
    });
  }

  private duplicateSelected(): void {
    const keys = [...this.selectedKeys];
    if (keys.length > 1) {
      const cmds: RezCmd[] = [];
      for (const k of keys) {
        const row = this.rowForKey(k);
        if (row) cmds.push(this.makeDuplicateCmd(snapshotPart(row)));
      }
      if (!cmds.length) return;
      void this.stack.run(new GroupCmd(`Duplicate ${cmds.length} parts`, cmds)).catch((err: Error) => {
        bus.emit('atlas:status', { text: `Duplicate rejected: ${err.message.slice(0, 100)}` });
      });
      return;
    }
    const key = this.selectedKey;
    if (!key) return;
    const row = this.rowForKey(key);
    if (!row) return;
    const s = snapshotPart(row);
    const base = {
      worldId: s.worldId,
      posX: s.posX + 0.5, posY: s.posY, posZ: s.posZ,
      rotX: s.rotX, rotY: s.rotY, rotZ: s.rotZ, rotW: s.rotW,
      scaleX: s.scaleX, scaleY: s.scaleY, scaleZ: s.scaleZ,
      physics: s.physics,
    };
    void this.stack.run(this.makeDuplicateCmd(s)).catch((err: Error) => {
      bus.emit('atlas:status', { text: `Duplicate rejected: ${err.message.slice(0, 100)}` });
    });
  }

  private makeDuplicateCmd(s: PartRowSnapshot): RezCmd {
    const base = {
      worldId: s.worldId,
      posX: s.posX + 0.5, posY: s.posY, posZ: s.posZ,
      rotX: s.rotX, rotY: s.rotY, rotZ: s.rotZ, rotW: s.rotW,
      scaleX: s.scaleX, scaleY: s.scaleY, scaleZ: s.scaleZ,
      physics: s.physics,
    };
    return s.prefabKey
      ? new RezCmd(`Duplicate #${s.partId}`, { ...base, prefabKey: s.prefabKey }, true)
      : new RezCmd(`Duplicate #${s.partId}`, { ...base, primType: s.primType, shapeJson: s.shapeJson, materialJson: s.materialJson }, false);
  }

  /** End key — drop the part onto whatever is beneath it (ground or parts). */
  private dropToFloor(): void {
    const key = this.selectedKey;
    if (!key) return;
    const mesh = this.meshForKey(key);
    if (!mesh) return;
    const bbox = new THREE.Box3().setFromObject(mesh);
    const halfH = (bbox.max.y - bbox.min.y) / 2;
    const origin = mesh.getWorldPosition(new THREE.Vector3());
    origin.y = bbox.min.y - 0.01;
    this.raycaster.set(origin, new THREE.Vector3(0, -1, 0));
    const hits = this.raycaster
      .intersectObjects(this.scene.children, true)
      .filter((h) => {
        let o: THREE.Object3D | null = h.object;
        while (o) { if (o === mesh) return false; o = o.parent; }
        return true;
      });
    if (!hits.length) return;
    const groundY = hits[0].point.y;
    this.applyEdit((a) => { a.posY = groundY + halfH; }, `Drop #${key} to floor`);
  }

  /** F — turn the avatar toward the selected part (follow-cam frames it). */
  private focusSelected(): void {
    const key = this.selectedKey;
    if (!key) return;
    const mesh = this.meshForKey(key);
    const body = atlas.localMesh;
    if (!mesh || !body) return;
    const p = mesh.getWorldPosition(new THREE.Vector3());
    const dx = p.x - body.position.x;
    const dz = p.z - body.position.z;
    body.rotation.y = Math.atan2(dx, dz);
    const dist = Math.hypot(dx, dz).toFixed(1);
    bus.emit('atlas:status', { text: `Facing part #${key} (${dist} m away).` });
  }

  // ─── Build Editor window (outliner · inspector · budget · issues) ─────────────

  private openPanel(): void {
    if (this.win) {
      this.win.setOpen(true);
      this.renderPanel();
      return;
    }
    this.win = makeWindow({ id: 'build-editor', title: 'Build Editor', width: 300, onClose: () => { if (this.editing) this.setEditing(false); } });
    const body = this.win.body;
    body.innerHTML =
      `<div id="be-budget" style="padding:4px 6px;border-bottom:1px solid var(--ax-line)"></div>` +
      `<div id="be-history" style="padding:2px 6px;color:var(--ax-muted);font-size:11px"></div>` +
      `<div style="padding:4px 6px"><input id="be-filter" placeholder="filter parts…" style="width:100%;background:var(--ax-bg);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:2px 6px"/></div>` +
      `<div id="be-outliner" style="max-height:160px;overflow:auto;padding:2px 6px"></div>` +
      `<div id="be-inspector" style="border-top:1px solid var(--ax-line);padding:4px 6px"></div>` +
      `<div id="be-issues" style="border-top:1px solid var(--ax-line);padding:4px 6px;max-height:120px;overflow:auto"></div>` +
      `<div id="be-snap" style="border-top:1px solid var(--ax-line);padding:4px 6px;color:var(--ax-muted);font-size:11px"></div>`;
    this.win.setOpen(true);
    for (const id of ['be-budget', 'be-history', 'be-filter', 'be-outliner', 'be-inspector', 'be-issues', 'be-snap']) {
      this.els[id] = body.querySelector(`#${id}`) as HTMLElement;
    }
    (this.els['be-filter'] as HTMLInputElement).addEventListener('input', (e) => {
      this.outlinerFilter = (e.target as HTMLInputElement).value.toLowerCase();
      this.renderOutliner();
    });
    this.renderPanel();
  }

  /** HTML-escape untrusted strings (meshRef on uploaded meshes is user text). */
  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  }

  private worldParts(): PartRowLike[] {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return [];
    const out: PartRowLike[] = [];
    for (const p of conn.db.part.iter() as Iterable<PartRowLike>) {
      if (p.worldId === wid) out.push(p);
    }
    return out;
  }

  private renderPanel(): void {
    if (!this.win) return;
    this.renderBudget();
    this.renderHistory();
    this.renderOutliner();
    this.renderInspector();
    this.renderIssues();
    this.els['be-snap'].textContent =
      `Snap [X]: ${this.snapEnabled ? 'ON (1 m · 15° · 0.25)' : 'off'} · W/E/R modes · Ctrl+D dup · End drop · F face`;
  }

  private renderBudget(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const b = conn.db.worldBuildBudget.worldId.find(wid) as { liUsed: number; liMax: number } | undefined;
    const used = b?.liUsed ?? 0;
    const max = b?.liMax ?? 750;
    const pct = Math.min(100, Math.round((used / Math.max(1, max)) * 100));
    const color = pct >= 95 ? 'var(--ax-bad)' : pct >= 75 ? 'var(--ax-warn)' : 'var(--ax-good)';
    this.els['be-budget'].innerHTML =
      `<div style="display:flex;justify-content:space-between;font-size:12px"><span>Land Impact</span><span>${used} / ${max}</span></div>` +
      `<div style="height:5px;background:var(--ax-bg3);border-radius:var(--ax-radius);margin-top:2px"><div style="width:${pct}%;height:100%;background:${color};border-radius:var(--ax-radius)"></div></div>`;
  }

  private renderHistory(): void {
    if (!this.els['be-history']) return;
    this.els['be-history'].textContent =
      `Undo ${this.stack.depth} · last: ${this.stack.lastLabel}` +
      (this.stack.canRedo ? ' · redo available' : '');
  }

  private renderOutliner(): void {
    const el = this.els['be-outliner'];
    if (!el) return;
    const parts = this.worldParts()
      .filter((p) => !this.outlinerFilter ||
        `${p.primType} ${p.meshRef} ${p.partId}`.toLowerCase().includes(this.outlinerFilter));
    el.innerHTML = parts.length ? '' : '<div style="color:var(--ax-muted)">No parts in this world.</div>';
    for (const p of parts.slice(0, 200)) {
      const key = p.partId.toString();
      const row = document.createElement('div');
      const sel = key === this.selectedKey;
      row.style.cssText = `display:flex;justify-content:space-between;padding:1px 4px;border-radius:var(--ax-radius);cursor:pointer;font-size:12px;${sel ? 'background:color-mix(in srgb, var(--ax-accent) 20%, transparent)' : ''}`;
      const label = this.esc(p.primType === 'mesh' ? (p.meshRef || 'mesh') : p.primType);
      row.innerHTML = `<span>${label} <span style="color:var(--ax-muted)">#${key}</span></span><span style="color:var(--ax-muted)">LI ${p.landImpact}</span>`;
      row.addEventListener('click', (ev) => {
        if ((ev as MouseEvent).shiftKey) {
          const keys = new Set(this.selectedKeys);
          if (keys.has(key)) keys.delete(key); else keys.add(key);
          this.setSelection([...keys], key);
        } else { this.select(key); this.focusSelected(); }
      });
      el.appendChild(row);
    }
  }

  /** Generated inspector: numeric transform fields + physics, all through the
   *  command stack (typed edits coalesce per part). */
  private renderInspector(): void {
    const el = this.els['be-inspector'];
    if (!el) return;
    const key = this.selectedKey;
    const row = key ? this.rowForKey(key) : null;
    if (!key || !row) {
      el.innerHTML = '<div style="color:var(--ax-muted);font-size:12px">Click a part to edit it.</div>';
      return;
    }
    const euler = new THREE.Euler().setFromQuaternion(new THREE.Quaternion(row.rotX, row.rotY, row.rotZ, row.rotW), 'YXZ');
    const num = (id: string, label: string, v: number) =>
      `<label style="display:flex;gap:4px;align-items:center;font-size:11px;color:var(--ax-muted)">${label}` +
      `<input id="${id}" type="number" step="0.1" value="${v.toFixed(2)}" style="width:62px;background:var(--ax-bg);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:1px 3px"/></label>`;
    el.innerHTML =
      `<div style="font-size:12px;margin-bottom:3px">${this.esc(row.primType === 'mesh' ? row.meshRef : row.primType)} <span style="color:var(--ax-muted)">#${key}</span></div>` +
      `<div style="display:flex;gap:4px;flex-wrap:wrap">` +
      num('be-px', 'X', row.posX) + num('be-py', 'Y', row.posY) + num('be-pz', 'Z', row.posZ) +
      num('be-ry', 'Yaw°', THREE.MathUtils.radToDeg(euler.y)) +
      num('be-s', 'Scale', row.scaleX) +
      `</div>` +
      `<label style="font-size:11px;color:var(--ax-muted);display:block;margin-top:3px"><input id="be-phys" type="checkbox" ${row.physics ? 'checked' : ''}/> physics collider</label>`;
    const commit = () => {
      const g = (id: string) => parseFloat((el.querySelector(`#${id}`) as HTMLInputElement).value) || 0;
      const yaw = THREE.MathUtils.degToRad(g('be-ry'));
      const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(euler.x, yaw, euler.z, 'YXZ'));
      const s = Math.max(0.05, g('be-s'));
      this.applyEdit((a) => {
        a.posX = g('be-px'); a.posY = g('be-py'); a.posZ = g('be-pz');
        a.rotX = q.x; a.rotY = q.y; a.rotZ = q.z; a.rotW = q.w;
        a.scaleX = s; a.scaleY = s; a.scaleZ = s;
        a.physics = (el.querySelector('#be-phys') as HTMLInputElement).checked;
      }, `Edit #${key}`);
    };
    for (const input of el.querySelectorAll('input')) {
      input.addEventListener('change', commit);
      input.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') commit(); });
    }
  }

  /** Live numeric refresh during a gizmo drag (no commit — display only). */
  private renderInspectorNumbers(): void {
    const el = this.els['be-inspector'];
    const mesh = this.gizmo?.object;
    if (!el || !mesh) return;
    const set = (id: string, v: number) => {
      const inp = el.querySelector(`#${id}`) as HTMLInputElement | null;
      if (inp && document.activeElement !== inp) inp.value = v.toFixed(2);
    };
    set('be-px', mesh.position.x); set('be-py', mesh.position.y); set('be-pz', mesh.position.z);
    set('be-s', mesh.scale.x);
  }

  /** Edit-time validation with click-to-select (the issue-browser pattern). */
  private computeIssues(): Issue[] {
    const issues: Issue[] = [];
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return issues;
    const b = conn.db.worldBuildBudget.worldId.find(wid) as { liUsed: number; liMax: number } | undefined;
    if (b && b.liUsed > b.liMax) {
      issues.push({ severity: 'error', text: `Over Land-Impact budget (${b.liUsed}/${b.liMax}) — edits will be rejected.`, partKey: null });
    }
    const world = conn.db.world.worldId.find(wid) as { sizeM: number } | undefined;
    const half = world ? world.sizeM / 2 : Infinity;
    for (const p of this.worldParts()) {
      const key = p.partId.toString();
      if (Math.abs(p.posX) > half || Math.abs(p.posZ) > half) {
        issues.push({ severity: 'error', text: `#${key} is outside the world bounds.`, partKey: key });
        continue;
      }
      const mesh = this.meshForKey(key);
      if (!mesh) continue;
      const bbox = new THREE.Box3().setFromObject(mesh);
      const origin = new THREE.Vector3(p.posX, bbox.min.y - 0.01, p.posZ);
      this.raycaster.set(origin, new THREE.Vector3(0, -1, 0));
      const hits = this.raycaster.intersectObjects(this.scene.children, true).filter((h) => {
        let o: THREE.Object3D | null = h.object;
        while (o) { if (o === mesh) return false; o = o.parent; }
        return true;
      });
      if (hits.length && hits[0].distance > 1.5) {
        issues.push({ severity: 'warn', text: `#${key} floats ${hits[0].distance.toFixed(1)} m above ground.`, partKey: key });
      }
    }
    return issues.slice(0, 20);
  }

  private renderIssues(): void {
    const el = this.els['be-issues'];
    if (!el) return;
    const issues = this.computeIssues();
    el.innerHTML = `<div style="font-size:11px;color:var(--ax-muted);margin-bottom:2px">Issues (${issues.length})</div>`;
    if (!issues.length) {
      el.innerHTML += '<div style="font-size:12px;color:var(--ax-good)">No problems found.</div>';
      return;
    }
    for (const i of issues) {
      const row = document.createElement('div');
      row.style.cssText = `font-size:12px;padding:1px 4px;border-radius:var(--ax-radius);cursor:${i.partKey ? 'pointer' : 'default'};color:${i.severity === 'error' ? 'var(--ax-bad)' : 'var(--ax-hi)'}`;
      row.textContent = i.text;
      if (i.partKey) row.addEventListener('click', () => { this.select(i.partKey!); this.focusSelected(); });
      el.appendChild(row);
    }
  }

  // ─── Keys ─────────────────────────────────────────────────────────────────────

  private onKey(e: KeyboardEvent): void {
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

    if (e.code === 'KeyB' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      this.setEditing(!this.editing);
      return;
    }
    if (!this.editing) return;

    // Undo/redo — total, through the single command stack.
    if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') {
      e.preventDefault();
      void (e.shiftKey ? this.stack.redo() : this.stack.undo());
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.code === 'KeyY') {
      e.preventDefault();
      void this.stack.redo();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.code === 'KeyD') {
      e.preventDefault();
      this.duplicateSelected();
      return;
    }
    if (e.ctrlKey || e.metaKey) return;

    switch (e.code) {
      case 'Escape':
        e.preventDefault();
        if (this.selectedKey) this.deselect();
        else this.setEditing(false);
        break;
      case 'Delete':
      case 'Backspace':
        if (this.selectedKey) { e.preventDefault(); this.deleteSelected(); }
        break;
      case 'KeyW': case 'Digit1':
        if (this.gizmo && this.selectedKey) { e.preventDefault(); this.gizmo.setMode('translate'); }
        break;
      case 'KeyE': case 'Digit2':
        if (this.gizmo && this.selectedKey) { e.preventDefault(); this.gizmo.setMode('rotate'); }
        break;
      case 'KeyR': case 'Digit3':
        if (this.gizmo && this.selectedKey) { e.preventDefault(); this.gizmo.setMode('scale'); }
        break;
      case 'KeyQ':
        if (this.selectedKey) { e.preventDefault(); this.deselect(); }
        break;
      case 'KeyF':
        if (this.selectedKey) { e.preventDefault(); this.focusSelected(); }
        break;
      case 'End':
        if (this.selectedKey) { e.preventDefault(); this.dropToFloor(); }
        break;
      case 'KeyX':
        e.preventDefault();
        this.snapEnabled = !this.snapEnabled;
        this.applySnap();
        break;
      default:
        break;
    }
  }

  dispose(): void {
    this.deselect();
    this.win?.setOpen(false);
    if (this.gizmoHelper) this.scene.remove(this.gizmoHelper);
    this.gizmo?.dispose();
    this.gizmo = null;
  }
}
